import { ScaleIcon, HeartHandshakeIcon, CompassIcon } from 'lucide-react'
import type { PillarPageContent } from '../../types/pillarPage'

export const cuerpoPage: PillarPageContent = {
  slug: 'cuerpo',
  accent: { base: '111 150 121', deep: '27 42 32', ink: '8 9 9' },
  affiliateUrl: '',

  hero: {
    eyebrow: 'Cuerpo · CreceElite',
    headlineTop: 'Tu cuerpo no es tu posesión.',
    headlineAccent: 'Es tu asignación.',
    quote:
      '“Dios te dio un cuerpo para vivir, servir, crear, amar y cumplir tu propósito. Cuidarlo no es vanidad. Es responsabilidad.”',
    ctaLabel: 'Comenzar mi transformación',
    tags: ['Disciplina física', 'Energía', 'Fuerza', 'Descanso'],
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/efb8754c-d5da-4841-904f-3a7323a4502a.jpg',
    imageAlt: 'Persona entrenando en un espacio oscuro con iluminación cenital',
  },

  perspective: {
    eyebrow: 'Cambio de perspectiva',
    titleTop: 'No se trata de verte mejor.',
    titleSub: 'Se trata de poder vivir mejor.',
    paragraphs: [
      'Puedes tener grandes sueños, grandes ideas y un propósito enorme. Pero necesitas energía para ejecutarlos.',
      'Tu cuerpo te acompaña cada día. Es el instrumento a través del cual trabajas, construyes, sirves, amas y disfrutas la vida.',
      'Por eso, en CreceElite no vemos el bienestar físico como una obsesión por la apariencia.',
    ],
    closingParagraph: 'Lo vemos como una forma de administración.',
    highlight: ['Cuida tu cuerpo.', 'Fortalece tu capacidad.', 'Honra tu asignación.'],
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/fa974f13-7ba0-4afd-bfa0-ebe47ac4fbb3.jpg',
    imageAlt: 'Manos atando las zapatillas antes de entrenar',
    caption: 'Administración, no apariencia',
  },

  foundation: {
    eyebrow: 'El cuerpo como templo',
    title: 'Tu cuerpo merece cuidado porque tu vida tiene propósito.',
    quote: '“¿O ignoráis que vuestro cuerpo es templo del Espíritu Santo...?”',
    quoteSource: '1 Corintios 6:19',
    lead: 'El objetivo no es perseguir un cuerpo perfecto.',
    closing:
      'El objetivo es desarrollar un cuerpo capaz de acompañarte en la misión que Dios puso delante de ti.',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/d9104a52-c1a2-43eb-9808-b31de529da69.jpg',
    imageAlt: 'Persona descansando en calma después de entrenar en una sala oscura',
    virtues: [
      {
        label: 'Disciplina',
        text: 'Hacer lo correcto incluso cuando no tienes ganas.',
        icon: ScaleIcon,
      },
      {
        label: 'Cuidado',
        text: 'Tratar tu cuerpo como algo que merece atención y respeto.',
        icon: HeartHandshakeIcon,
      },
      {
        label: 'Dominio propio',
        text: 'Aprender a gobernar tus decisiones en lugar de ser gobernado por ellas.',
        icon: CompassIcon,
      },
    ],
  },

  pillars: {
    eyebrow: 'Los 3 pilares',
    title: 'Un cuerpo fuerte se construye sobre tres fundamentos.',
    items: [
      {
        number: '01',
        label: 'Nutrición',
        headline: 'Alimenta el cuerpo que necesitas para construir tu futuro.',
        text: 'La alimentación no debería ser una batalla constante. Aprende a darle a tu cuerpo los nutrientes y la energía que necesita para funcionar y recuperarse.',
      },
      {
        number: '02',
        label: 'Movimiento',
        headline: 'La fuerza se construye. La capacidad también.',
        text: 'Entrenar no consiste solamente en levantar peso o cambiar tu apariencia. Consiste en desarrollar fuerza, resistencia, movilidad y disciplina.',
      },
      {
        number: '03',
        label: 'Recuperación',
        headline: 'El descanso también es disciplina.',
        text: 'Entrenar sin recuperar no es disciplina. Dormir y descansar correctamente forman parte del proceso de construir un cuerpo sostenible.',
      },
    ],
  },

  purpose: {
    eyebrow: 'El verdadero objetivo',
    title: '¿Para qué quieres un cuerpo fuerte?',
    reasons: [
      { label: 'Para tener energía', text: 'Para levantarte cada día con más vitalidad.' },
      { label: 'Para tu familia', text: 'Para jugar, compartir y estar presente.' },
      { label: 'Para tu trabajo', text: 'Para tener la capacidad física y mental de construir.' },
      {
        label: 'Para tu futuro',
        text: 'Para llegar a los próximos años con independencia y calidad de vida.',
      },
    ],
    statementLead: 'No entrenas solamente para el cuerpo que quieres tener.',
    statementAccent: 'Entrenas para la vida que quieres vivir.',
  },

  discipline: {
    eyebrow: 'Disciplina',
    titleTop: 'La motivación te inicia.',
    titleAccent: 'La disciplina te transforma.',
    paragraphs: [
      'Habrá días en los que no tendrás ganas.',
      'Días en los que será más fácil abandonar.',
    ],
    closingParagraph:
      'Pero la transformación ocurre cuando tus decisiones dejan de depender de cómo te sientes.',
    sequence: ['Decisión', 'Hábito', 'Disciplina', 'Resultado'],
    closingLead: 'No necesitas hacerlo perfecto.',
    closingAccent: 'Necesitas hacerlo consistentemente.',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/2f735732-0766-4581-9258-084e17b30c15.jpg',
  },

  path: {
    eyebrow: 'Ahora necesitas un camino',
    title: 'Ahora necesitas un camino.',
    paragraphs: [
      'Saber que necesitas cambiar es solamente el comienzo.',
      'El siguiente paso es aprender qué hacer, cómo hacerlo y cómo mantenerlo en el tiempo.',
    ],
    steps: [
      { label: 'Entrenamiento', text: 'Aprende a entrenar de manera estructurada.' },
      { label: 'Alimentación', text: 'Aprende a tomar mejores decisiones nutricionales.' },
      { label: 'Hábitos', text: 'Construye una rutina que puedas mantener.' },
      { label: 'Mentalidad', text: 'Deja de depender exclusivamente de la motivación.' },
      { label: 'Constancia', text: 'Convierte pequeñas acciones en resultados sostenibles.' },
    ],
  },

  formation: {
    eyebrow: 'El siguiente paso',
    title: 'No tienes que descubrirlo todo solo.',
    paragraphs: [
      'Una de las mejores decisiones que puedes tomar es aprender de personas que ya han recorrido este camino.',
      'Por eso, hemos seleccionado una formación que puede ayudarte a desarrollar mejores hábitos, estructurar tu entrenamiento y avanzar hacia una versión más fuerte y disciplinada de ti mismo.',
    ],
    cardEyebrow: 'Formación recomendada',
    cardTitle: 'Desarrolla tu cuerpo',
    cardQuote: '“Aprende. Entrena. Mejora. Repite.”',
    highlights: ['Aprende.', 'Entrena.', 'Mejora.', 'Repite.'],
    ctaLabel: 'Conocer el método',
    note: 'Una recomendación de CreceElite para quienes quieren estructura, no atajos.',
  },

  identity: {
    eyebrow: 'Identidad',
    title: '¿Quién estás decidiendo ser?',
    before: [
      'Postergar',
      'Falta de energía',
      'Hábitos desordenados',
      'Excusas',
      'Depender de la motivación',
    ],
    after: ['Disciplina', 'Energía', 'Estructura', 'Constancia', 'Dominio propio'],
    statementLead: 'La transformación física comienza mucho antes de que cambie tu cuerpo.',
    statementAccent: 'Comienza cuando cambia tu identidad.',
  },

  manifesto: {
    eyebrow: 'Manifiesto CreceElite',
    title: 'Mi cuerpo es mi responsabilidad',
    lines: [
      'No entreno por vanidad. Entreno porque tengo un propósito.',
      'No busco perfección. Busco disciplina.',
      'No castigo mi cuerpo. Lo cuido.',
      'No dependo de la motivación. Construyo hábitos.',
      'No busco solamente verme fuerte. Quiero ser capaz.',
      'Porque todavía tengo mucho por vivir. Mucho por construir. Mucho por entregar.',
    ],
  },

  finalCta: {
    headlineTop: 'Tu cuerpo es el vehículo.',
    headlineAccent: 'Tu propósito es el destino.',
    paragraph:
      'Ahora que entiendes la importancia de cuidar tu cuerpo, el siguiente paso es desarrollar un sistema que puedas llevar a la práctica.',
    ctaLabel: 'Comenzar mi camino',
    subnote: 'Accede a la formación recomendada por CreceElite.',
  },

  stickyCtaLabel: 'Comenzar mi camino',
}
